import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface BatchStock {
  stockId: number;
  posId: number;
  posCode: string;
  productId: number;
  productName: string;
  batchId: number;
  batchCode: string;
  stock: number;
  availableStock: number;
  expiryDate: string;
  measurementType: string;
  expiresInDays: number;
}

interface ViewBatchesDialogProps {
  visible: boolean;
  productId: number | null;
  posId: number | null;
  productName: string;
  onHide: () => void;
}

const ViewBatchesDialog: React.FC<ViewBatchesDialogProps> = ({
  visible,
  productId,
  posId,
  productName,
  onHide,
}) => {
  const toast = useRef<Toast>(null);
  const [batches, setBatches] = useState<BatchStock[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchBatches = async () => {
    if (!productId || !posId) return;

    try {
      setLoading(true);
      const payload = {
        posId,
        productId,
        showAll,
      };
debugger
      const response = await posService.getAllPOSproductBatch(payload);

      if (response?.isSuccess) {
        setBatches(response.stocks || []);
        setTotalCount(response.totalCount || 0);
      } else {
        showMessage("Error", response?.message || "Failed to load batches", "error");
        setBatches([]);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      showMessage("Error", "An error occurred while fetching batches", "error");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && productId && posId) {
      fetchBatches();
    }
  }, [visible, productId, posId, showAll]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const expiryDateTemplate = (rowData: BatchStock) => {
    return formatDate(rowData.expiryDate);
  };

  const stockTemplate = (rowData: BatchStock) => {
    return rowData.stock.toFixed(2);
  };

  const availableStockTemplate = (rowData: BatchStock) => {
    return rowData.availableStock.toFixed(2);
  };

  const dialogHeader = (
    <div>
      <h5 className="m-0">Batches of {productName}</h5>
      <small className="text-gray-500">Total: {totalCount} batch(es)</small>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header={dialogHeader}
        modal
        style={{ width: "70vw" }}
        breakpoints={{ "960px": "85vw", "641px": "95vw" }}
      >
        <div className="mb-3">
          <div className="flex align-items-center">
            <Checkbox
              inputId="showAll"
              checked={showAll}
              onChange={(e) => setShowAll(e.checked || false)}
            />
            <label htmlFor="showAll" className="ml-2 cursor-pointer">
              Show All Batches
            </label>
          </div>
        </div>

        <DataTable
          value={batches}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          className="p-datatable-striped"
          emptyMessage="No batches found"
        >
          <Column
            field="batchCode"
            header="Batch Code"
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="stock"
            header="Stock"
            body={stockTemplate}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="availableStock"
            header="Available Stock"
            body={availableStockTemplate}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="expiryDate"
            header="Expiry Date"
            body={expiryDateTemplate}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="expiresInDays"
            header="Expires In Days"
            body={(rowData) => rowData.expiresInDays}
            sortable
            style={{ width: "30%" }}
          />
        </DataTable>
      </Dialog>
    </>
  );
};

export default ViewBatchesDialog;
